import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Alert from '../common/Alert.js';
import { testimonialService } from '../../services/testimonialService.js';
import { MessageSquareQuote } from 'lucide-react';
import { FormLayout, FormField } from '../common/index.js';

const AddTestimonial = () => {
  const [formData, setFormData] = useState({
    full_name: '',
    contact_number: '',
    email: '',
    location: '',
    rating: '5',
    testimonial_text: '',
    is_active: true,
  });
  const [imagePreview, setImagePreview] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 1048576) {
        Alert.error('Image size must be less than 1MB');
        e.target.value = '';
        return;
      }
      const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
        Alert.error('Only PNG, JPG, JPEG, GIF, and WebP images are allowed');
        e.target.value = '';
        return;
      }
      const reader = new FileReader();
      reader.onloadend = () => setImagePreview(reader.result);
      reader.readAsDataURL(file);
    }
  };

  const validateForm = () => {
    if (formData.contact_number && !/^\d{10}$/.test(formData.contact_number)) {
      Alert.error('Contact number must be exactly 10 digits');
      return false;
    }
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      Alert.error('Please enter a valid email address');
      return false;
    }
    if (!formData.contact_number && !formData.email) {
      Alert.error('Please provide either contact number or email');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    if (!validateForm()) return;

    setLoading(true);
    try {
      const form = new FormData();
      form.append('full_name', formData.full_name);
      form.append('location', formData.location);
      form.append('rating', formData.rating);
      form.append('testimonial_text', formData.testimonial_text);
      form.append('is_active', formData.is_active);
      if (formData.contact_number) form.append('contact_number', formData.contact_number);
      if (formData.email) form.append('email', formData.email);
      if (e.target.image.files[0]) form.append('image', e.target.image.files[0]);

      await testimonialService.createTestimonial(form);
      Alert.success('Testimonial created successfully!');
      setTimeout(() => navigate('/testimonials'), 1500);
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to create testimonial');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormLayout
      title="Add New Testimonial"
      icon={MessageSquareQuote}
      onBack={() => navigate('/testimonials')}
      onSubmit={handleSubmit}
      submitLabel={loading ? 'Creating...' : 'Create Testimonial'}
      onCancel={() => navigate('/testimonials')}
      loading={loading}
    >
      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200">
          Customer Information
        </h3>
        <div className="space-y-4">
          <FormField label="Full Name" required>
            <input type="text" name="full_name" value={formData.full_name} onChange={handleChange} placeholder="Enter customer name" required className="input-clean w-full" />
          </FormField>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField label="Contact Number">
              <input type="text" name="contact_number" value={formData.contact_number} onChange={handleChange} placeholder="Enter 10-digit number" maxLength={10} className="input-clean w-full" />
            </FormField>
            <FormField label="Email Address">
              <input type="email" name="email" value={formData.email} onChange={handleChange} placeholder="customer@example.com" className="input-clean w-full" />
            </FormField>
          </div>
          <div className="bg-brand-light border border-blue-200 rounded-card p-3 text-sm text-blue-800">
            At least one contact method (phone or email) is required.
          </div>
          <FormField label="Location" required>
            <input type="text" name="location" value={formData.location} onChange={handleChange} placeholder="Enter location/city" required className="input-clean w-full" />
          </FormField>
          <FormField label="Customer Image (Optional)">
            <input type="file" name="image" accept="image/*" onChange={handleImageChange} className="input-clean w-full" />
            {imagePreview && (
              <img src={imagePreview} alt="Preview" className="w-24 h-24 rounded-full object-cover border-2 border-gray-200 mt-3" />
            )}
          </FormField>
        </div>
      </div>

      <div>
        <h3 className="text-base font-semibold text-secondary mb-4 pb-2 border-b border-gray-200">
          Testimonial Details
        </h3>
        <div className="space-y-4">
          <FormField label="Rating" required>
            <select name="rating" value={formData.rating} onChange={handleChange} className="input-clean w-full">
              <option value="5">5 ⭐⭐⭐⭐⭐</option>
              <option value="4">4 ⭐⭐⭐⭐</option>
              <option value="3">3 ⭐⭐⭐</option>
              <option value="2">2 ⭐⭐</option>
              <option value="1">1 ⭐</option>
            </select>
          </FormField>
          <FormField label="Testimonial Text" required>
            <textarea name="testimonial_text" value={formData.testimonial_text} onChange={handleChange} placeholder="Enter the customer's testimonial" rows={5} required className="input-clean w-full resize-none" />
          </FormField>
          <FormField label="Active Status" required>
            <select name="is_active" value={formData.is_active} onChange={handleChange} className="input-clean w-full">
              <option value={true}>Active</option>
              <option value={false}>Inactive</option>
            </select>
          </FormField>
        </div>
      </div>
    </FormLayout>
  );
};

export default AddTestimonial;
